// The add-a-passkey prompt (see `features/passkey-login.md`): after every
// fresh password or magic-link sign-in, a user who has no passkey yet is
// offered one — skippable, but asked again on their next such sign-in until
// they enrol. The copy leads with how easy passkeys are and stresses that a
// passkey lives on one device, so each device needs its own. Mounted once at
// the app root (`App.tsx`), above the admin/athlete surface fork, so every
// kind of account gets it.
//
// The cue is `passkeyPrompt` from `useAuth` — set only by a fresh password or
// email-link sign-in (never a page refresh, never a passkey sign-in). This
// component then checks the account's passkey count and stays silent unless
// it is zero, so enrolled users never see a flash.

import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { MAX_PASSKEYS_PER_USER } from '../domain';
import {
  addPasskeyCeremony,
  fetchMyPasskeys,
  isPasskeyCancelled,
  passkeysSupported,
} from '../data/passkeyClient';
import { useAuth } from '../state/AuthContext';
import { useTranslation } from '../state/I18nContext';

type Phase = 'closed' | 'offer' | 'busy' | 'done';

export function PasskeyPromptDialog() {
  const { passkeyPrompt, dismissPasskeyPrompt } = useAuth();
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('closed');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  // A fresh sign-in landed: prompt only a passkey-less account on a capable
  // browser. Any failure here silently stands down — the nag must never get
  // in the way of using the app.
  useEffect(() => {
    if (!passkeyPrompt) {
      setPhase('closed');
      setName('');
      setError(null);
      return;
    }
    if (!passkeysSupported()) {
      dismissPasskeyPrompt();
      return;
    }
    let stale = false;
    void fetchMyPasskeys()
      .then(({ passkeys }) => {
        if (stale) return;
        if (passkeys.length === 0) setPhase('offer');
        else dismissPasskeyPrompt();
      })
      .catch(() => {
        if (!stale) dismissPasskeyPrompt();
      });
    return () => {
      stale = true;
    };
  }, [passkeyPrompt, dismissPasskeyPrompt]);

  const add = async () => {
    setPhase('busy');
    setError(null);
    try {
      await addPasskeyCeremony(name);
      setPhase('done');
    } catch (e: unknown) {
      // Backing out of the browser's passkey prompt just returns to the offer.
      if (!isPasskeyCancelled(e)) {
        setError(e instanceof Error ? e.message : t('passkeys.addFailed'));
      }
      setPhase('offer');
    }
  };

  return (
    <Dialog
      open={phase !== 'closed'}
      onClose={phase === 'busy' ? undefined : dismissPasskeyPrompt}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>{t('passkeys.prompt.title')}</DialogTitle>
      {phase === 'done' ? (
        <>
          <DialogContent>
            <Alert severity="success">{t('passkeys.added')}</Alert>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={dismissPasskeyPrompt}>
              {t('passkeys.prompt.done')}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t('passkeys.prompt.body')}
              </Typography>
              <Alert severity="info">
                {t('passkeys.prompt.perDevice', { max: MAX_PASSKEYS_PER_USER })}
              </Alert>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField
                label={t('passkeys.nameLabel')}
                placeholder={t('passkeys.namePlaceholder')}
                fullWidth
                size="small"
                value={name}
                disabled={phase === 'busy'}
                onChange={(e) => setName(e.target.value)}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={dismissPasskeyPrompt} disabled={phase === 'busy'}>
              {t('passkeys.prompt.skip')}
            </Button>
            <Button
              variant="contained"
              startIcon={<FingerprintIcon />}
              onClick={() => void add()}
              disabled={phase === 'busy'}
            >
              {phase === 'busy' ? t('passkeys.adding') : t('passkeys.prompt.add')}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
